const formatCounter = (index, total) => `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;

class PremiumProductGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.scroller = gallery.querySelector('slideshow-slides');
    this.slides = [...gallery.querySelectorAll('slideshow-slide.product-media-container')];
    this.progressFill = gallery.querySelector('.premium-gallery-progress__fill');
    this.counter = gallery.querySelector('[data-premium-gallery-counter]');
    this.activeIndex = 0;

    if (!this.scroller || this.slides.length < 2) return;

    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
    this.scroller.addEventListener('keydown', this.handleKeydown);
    this.scroller.setAttribute('aria-label', 'Product media gallery');
    this.scroller.setAttribute('role', 'region');

    this.observer = new IntersectionObserver(this.handleIntersect, {
      root: this.scroller,
      threshold: [0.55, 0.7, 0.85],
    });

    this.slides.forEach((slide) => this.observer.observe(slide));
    this.update(0);
  }

  handleIntersect = (entries) => {
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

    if (!visible) return;

    const index = this.slides.indexOf(visible.target);
    if (index >= 0) this.update(index);
  };

  handleScroll = () => {
    window.requestAnimationFrame(() => {
      const scrollerRect = this.scroller.getBoundingClientRect();
      const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const distance = Math.abs(center - scrollerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.update(closestIndex);
    });
  };

  handleKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = Math.min(Math.max(this.activeIndex + direction, 0), this.slides.length - 1);
    this.slides[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  update(index) {
    if (index === this.activeIndex && this.counter?.textContent) return;

    this.activeIndex = index;
    const total = this.slides.length;
    const progress = `${((index + 1) / total) * 100}%`;

    this.progressFill?.style.setProperty('--premium-gallery-progress', progress);

    if (this.counter) {
      this.counter.textContent = formatCounter(index, total);
    }
  }
}

const initPremiumProduct = (root = document) => {
  root.querySelectorAll('[data-premium-product-gallery]').forEach((gallery) => {
    if (gallery.dataset.premiumGalleryReady === 'true') return;

    gallery.dataset.premiumGalleryReady = 'true';
    new PremiumProductGallery(gallery);
  });
};

initPremiumProduct();

document.addEventListener('shopify:section:load', (event) => initPremiumProduct(event.target));
